import { ExpertDataQuery } from 'shared/hooks/api/expertData/expertData.generated';
import { Certificate } from 'types/Certificate';
import { Education } from 'types/Education';
import { AvailabilityEnum } from 'types/enums/availability-enum';
import { WorkTypeEnum } from 'types/enums/work-type.enum';
import { ExpertProfile } from 'types/ExpertProfile/ExpertProfile';
import { Language } from 'types/Language';
import { Skill } from 'types/Skill';
import { WorkHistory } from 'types/WorkHistory';

const mapDtoToLanguages = (dto: ExpertDataQuery['expertHubData']['languages']): Language[] =>
  dto.map(({ languageId, level }) => ({
    id: languageId,
    level,
  }));

const mapDtoToWorkHistory = (
  dto: ExpertDataQuery['expertHubData']['workHistory'][number],
): WorkHistory => ({
  id: dto.id,
  jobPositionId: dto.jobPositionId || '',
  companyName: dto.companyName || null,
  mainTechnologyIds: dto.mainTechnologyIds || [],
  tagIds: dto.tagsIds || [],
  dateStart: dto.dateStart ? new Date(dto.dateStart) : null,
  dateEnd: dto.dateEnd ? new Date(dto.dateEnd) : null,
  isCurrentJob: dto.isCurrentJob || false,
  description: dto.description || '',
});

const splitWorkHistory = (
  jobPositionId: ExpertDataQuery['expertHubData']['jobPositionId'],
  dto: ExpertDataQuery['expertHubData']['workHistory'],
): { mainWorkHistory: WorkHistory[]; otherWorkHistory: WorkHistory[] } =>
  dto.reduce(
    (acc, curr) => {
      if (jobPositionId === curr.jobPositionId) {
        acc.mainWorkHistory.push(mapDtoToWorkHistory(curr));
        return acc;
      }
      acc.otherWorkHistory.push(mapDtoToWorkHistory(curr));
      return acc;
    },
    {
      mainWorkHistory: [] as WorkHistory[],
      otherWorkHistory: [] as WorkHistory[],
    },
  );

const mapDtoToSkills = (dto: ExpertDataQuery['expertHubData']['tags']): Skill[] =>
  dto.map(({ id, weight }) => ({ tagId: id, weight }));

const mapDtoToCertificates = (
  dto: ExpertDataQuery['expertHubData']['certificates'],
): Certificate[] =>
  dto.map((item) => ({
    id: item.id,
    name: item.name || null,
    issuer: item.issuer || null,
    url: item.url || null,
    issueDate: item.issueDate ? new Date(item.issueDate) : null,
  }));

const mapDtoToEducation = (dto: ExpertDataQuery['expertHubData']['education']): Education[] =>
  dto.map((item) => ({
    id: item.id,
    schoolName: item.schoolName || '',
    fieldOfStudy: item.fieldOfStudyPL || null,
    dateFrom: item.dateFrom ? new Date(item.dateFrom) : null,
    dateTo: item.dateTo ? new Date(item.dateTo) : null,
    isStillStudying: item.isCurrent || false,
  }));

export const mapDtoToExpertProfile = (
  dto: ExpertDataQuery['expertHubData'],
): Omit<ExpertProfile, 'email' | 'id'> => {
  const { mainWorkHistory, otherWorkHistory } = splitWorkHistory(
    dto.jobPositionId,
    dto.workHistory,
  );

  return {
    cv: dto.cv ?? null,
    firstName: dto.firstName ?? '',
    lastName: dto.lastName ?? '',
    avatarUrl: dto.avatarUrl ?? null,
    location: dto.location ? { label: dto.location, value: 'none' } : null,
    rateDesired: dto.rateDesired ?? null,
    workType: (dto.workType as unknown as WorkTypeEnum) ?? null,
    availability: {
      unit: (dto.availability as unknown as AvailabilityEnum) ?? null,
      noticeLength: dto.noticeLength ?? null,
    },
    jobPositionId: dto.jobPositionId ?? null,
    firstLanguageId: dto.firstLanguageId ?? null,
    isOnlyFirstLanguage:
      dto.isOnlyFirstLanguage === null || dto.isOnlyFirstLanguage === undefined
        ? null
        : dto.isOnlyFirstLanguage,
    otherLanguages: mapDtoToLanguages(dto.languages),
    mainWorkHistory,
    otherWorkHistory,
    isMainRoleExperience: dto.isMainRoleExperience ?? null,
    isOtherRolesExperience: dto.isOtherRolesExperience ?? null,
    skills: mapDtoToSkills(dto.tags),
    manuallyAddedSkills: mapDtoToSkills(dto.manuallyAddedTags),
    certificates: mapDtoToCertificates(dto.certificates),
    education: mapDtoToEducation(dto.education),
  };
};
